import { HStack, Text } from "@chakra-ui/layout";
import { MenuItem, useDisclosure } from "@chakra-ui/react";
import { STATUS_TO_BADGE } from "features/credit-facility/settings/CreditFacilitySettingsDetail";
import { CreditFacilitySettingsLoanVersionFormDrawer } from "features/credit-facility/settings/CreditFacilitySettingsLoan/CreditFacilitySettingsLoanVersionFormDrawer";
import { CreditFacilitySettingsLoanVersionTable } from "features/credit-facility/settings/CreditFacilitySettingsLoan/CreditFacilitySettingsLoanVersionTable";
import { useCreditFacilityLoanVersionRetrieveSuspense } from "gen/endpoints/credit-facility/credit-facility";
import { useTransactionCalculationListSuspense } from "gen/endpoints/transactions/transactions";
import { PermissionTypeEnum } from "gen/model/permissionTypeEnum";
import { VersionStatusEnum } from "gen/model/versionStatusEnum";
import { useTranslation } from "react-i18next";
import { QueryBoundary } from "shared/api/QueryBoundary";
import { Restricted } from "shared/components/access/Restricted";
import {
  ContentBlock,
  ContentBlockActions,
  ContentBlockBody,
  ContentBlockGroup,
  ContentBlockHeader,
  ContentBlockMenu,
  ContentBlockTitle,
} from "shared/components/content/ContentBlock";
import Descriptions from "shared/components/descriptions/Descriptions";
import PageBody from "shared/components/layout/PageBody";
import PageContent from "shared/components/layout/PageContent";
import { PageHeader } from "shared/components/layout/PageHeader";
import { useRequiredStringParams } from "shared/hooks/useRequiredStringParams";
import { rootRoute } from "shared/navigation/Routes";
import { formatPercentageForTable } from "shared/utils/formattingUtils";
import { formatMoney } from "shared/utils/MoneyUtils";

export const CreditFacilitySettingsLoanVersion = () => {
  const { t } = useTranslation(["creditFacility", "common", "enums"]);
  const { fundFamilyId, creditFacilityId, loanId } = useRequiredStringParams();

  const {
    isOpen: isEditModalOpen,
    onOpen: onEditModalOpen,
    onClose: closeEditModal,
  } = useDisclosure();

  const { data: loanVersion } = useCreditFacilityLoanVersionRetrieveSuspense(
    fundFamilyId,
    creditFacilityId,
    loanId,
  );

  const { data: calculations } =
    useTransactionCalculationListSuspense(fundFamilyId);

  return (
    <PageContent>
      <PageHeader
        title={loanVersion.name}
        backButton={{
          title: t("creditFacility:settings.creditFacilitySettings"),
          path: rootRoute({})
            .fundFamilies({})
            .fundFamily({ fundFamilyId })
            .setup({})
            .creditFacilities({})
            .creditFacility({ creditFacilityId })
            .loan({}).$,
        }}
      />
      <PageBody>
        <ContentBlockGroup>
          <ContentBlock>
            <ContentBlockHeader>
              <ContentBlockTitle>
                <HStack>
                  <Text textStyle="bodyBold">
                    {t("creditFacility:settings.loans.loanSettings")}
                  </Text>
                  {loanVersion.version_status != VersionStatusEnum.completed &&
                    STATUS_TO_BADGE[
                      loanVersion.version_status as VersionStatusEnum
                    ]()}
                </HStack>
              </ContentBlockTitle>
              <Restricted permission={PermissionTypeEnum.config}>
                <ContentBlockActions>
                  <ContentBlockMenu>
                    <MenuItem onClick={onEditModalOpen}>
                      {t("common:edit")}
                    </MenuItem>
                  </ContentBlockMenu>
                </ContentBlockActions>
              </Restricted>
            </ContentBlockHeader>
            <ContentBlockBody variant="descriptions">
              <Descriptions>
                <Descriptions.Item
                  label={t("creditFacility:setup.loan.loanName")}
                >
                  {loanVersion.name}
                </Descriptions.Item>
                <Descriptions.Item
                  label={t(
                    "creditFacility:setup.loan.loanDrawAndPaydownIncrements",
                  )}
                >
                  {formatMoney(loanVersion.loan_draw_and_paydown_increments)}
                </Descriptions.Item>
                <Descriptions.Item
                  label={t("creditFacility:setup.loan.minimumDrawdownAmount")}
                >
                  {formatMoney(loanVersion.minimum_draw_amount)}
                </Descriptions.Item>
                <Descriptions.Item
                  label={t("creditFacility:setup.loan.minimumPaydownAmount")}
                >
                  {formatMoney(loanVersion.minimum_paydown_amount)}
                </Descriptions.Item>
                <Descriptions.Item
                  label={t("creditFacility:setup.loan.interestRateCalculation")}
                >
                  {calculations.find(
                    (calculation) =>
                      calculation.id ===
                      loanVersion.interest_rate_calculation_id,
                  )?.name || ""}
                </Descriptions.Item>
                <Descriptions.Item
                  label={t(
                    "creditFacility:setup.loan.interestAccrualGranularity.title",
                  )}
                >
                  {t(
                    `creditFacility:setup.loan.interestAccrualGranularity.options.${loanVersion.interest_accrual_granularity}`,
                  )}
                </Descriptions.Item>
                <Descriptions.Item
                  label={t("creditFacility:setup.loan.applicableMargin")}
                >
                  {formatPercentageForTable(loanVersion.applicable_margin)}
                </Descriptions.Item>
                <Descriptions.Item
                  label={t("creditFacility:setup.loan.spread")}
                >
                  {formatPercentageForTable(loanVersion.spread)}
                </Descriptions.Item>
                <Descriptions.Item
                  label={t("creditFacility:setup.loan.interestRateFloor")}
                >
                  {formatPercentageForTable(loanVersion.interest_rate_floor)}
                </Descriptions.Item>
                <Descriptions.Item
                  label={t("creditFacility:setup.loan.interestRateCeiling")}
                >
                  {formatPercentageForTable(loanVersion.interest_rate_ceiling)}
                </Descriptions.Item>
                <Descriptions.Item
                  label={t(
                    "creditFacility:setup.loan.referenceRateLookbackPeriod",
                  )}
                >
                  {loanVersion.reference_rate_lookback_period}
                </Descriptions.Item>
                <Descriptions.Item
                  label={t("creditFacility:setup.loan.rateDayCount.title")}
                >
                  {t(
                    `enums:InterestDayCountEnum.${loanVersion.rate_day_count}`,
                  )}
                </Descriptions.Item>
                <Descriptions.Item
                  label={t(
                    "creditFacility:setup.loan.chargeInterestOnDrawdown",
                  )}
                >
                  {loanVersion.charge_interest_on_drawdown_day}
                </Descriptions.Item>
                <Descriptions.Item
                  label={t("creditFacility:setup.loan.chargeInterestOnPaydown")}
                >
                  {loanVersion.charge_interest_on_paydown_day}
                </Descriptions.Item>
              </Descriptions>
            </ContentBlockBody>
          </ContentBlock>
        </ContentBlockGroup>
        <CreditFacilitySettingsLoanVersionTable />
        {isEditModalOpen && (
          <QueryBoundary>
            <CreditFacilitySettingsLoanVersionFormDrawer
              loan={loanVersion}
              onCloseComplete={closeEditModal}
            />
          </QueryBoundary>
        )}
      </PageBody>
    </PageContent>
  );
};
